import type { SolutionPageContent } from '../../types/solution'

export const foodBeverageSolution: SolutionPageContent = {
  slug: 'food-beverage',
  hero: {
    headline: 'Serve more customers and waste less food',
    subheadline: 'Take orders fast, track every ingredient, and know your real profit on each dish.',
    card: {
      type: 'list',
      title: 'Lunch service · Kacyiru',
      badge: { label: 'Live', tone: 'positive' },
      rows: [
        { label: 'Table 4', sublabel: '2 × Brochette, 1 × Isombe', value: 'RWF 13,500' },
        { label: 'Takeaway #212', sublabel: '1 × Chips & chicken', value: 'RWF 5,000' },
        { label: 'Open orders', value: '6', tone: 'brand' },
      ],
      footer: { label: 'Sales so far', value: 'RWF 214,000' },
    },
  },
  challenges: [
    {
      quote: 'Orders get lost between the counter and the kitchen.',
      detail: 'A missed order means a waiting customer and a meal we can’t charge for.',
    },
    {
      quote: 'We run out of ingredients in the middle of service.',
      detail: 'Someone runs to the market, and the dish is off the menu for hours.',
    },
    {
      quote: 'I don’t know which dishes actually make money.',
      detail: 'Prices went up at the market, but our menu prices stayed the same.',
    },
  ],
  helps: [
    {
      solves: 'Orders get lost',
      title: 'Orders by table and takeaway',
      copy: 'Open an order for each table or takeaway, add items as guests decide, and take payment when they’re ready. Nothing slips through.',
      product: 'sales-pos',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Open orders',
          rows: [
            { label: 'Table 2', sublabel: '3 items · 18 min', value: 'RWF 16,000' },
            { label: 'Table 4', sublabel: '3 items · 9 min', value: 'RWF 13,500' },
            { label: 'Takeaway #212', sublabel: 'Ready', value: 'RWF 5,000', tone: 'positive' },
          ],
          action: 'New order',
        },
      },
    },
    {
      solves: 'We run out of ingredients',
      title: 'Ingredient stock that updates as you sell',
      copy: 'Link dishes to their ingredients. Every sale deducts what was used, and you’re alerted before you run out.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Kitchen stock',
          badge: { label: '2 low', tone: 'warning' },
          rows: [
            { label: 'Chicken (kg)', value: '3.5 kg', pct: 18, tone: 'negative' },
            { label: 'Potatoes (kg)', value: '12 kg', pct: 30, tone: 'warning' },
            { label: 'Rice (kg)', value: '28 kg', pct: 72 },
          ],
          action: 'Reorder from supplier',
        },
      },
    },
    {
      solves: 'I don’t know which dishes make money',
      title: 'Profit on every dish',
      copy: 'See what each dish costs to make and what it earns, so you can adjust prices or portions with confidence.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Margin by dish · this month',
          rows: [
            { label: 'Brochette', value: '58%', pct: 58, tone: 'positive' },
            { label: 'Isombe & rice', value: '52%', pct: 52, tone: 'positive' },
            { label: 'Chips & chicken', value: '31%', pct: 31 },
            { label: 'Fresh juice', value: '18%', pct: 18, tone: 'warning' },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'From morning prep to closing time.',
    items: [
      { time: '6:30', title: 'Check kitchen stock', copy: 'See what’s low before the market run.' },
      { time: '11:30', title: 'Take lunch orders', copy: 'Open orders by table and takeaway.' },
      { time: '14:00', title: 'Accept payments', copy: 'Mobile money, card, or cash, split if needed.' },
      { time: '16:00', title: 'Record expenses', copy: 'Log market purchases and gas with a photo.' },
      { time: '22:00', title: 'Close the day', copy: 'Review sales, top dishes, and cash-up.' },
    ],
  },
  results: [
    { value: '+25%', label: 'more orders served during peak hours' },
    { value: '−30%', label: 'less food thrown away each week' },
    { value: '100%', label: 'of dishes with a known cost and margin' },
  ],
  testimonial: {
    quote: 'We stopped running out of chicken at lunch, and I finally know which dishes carry the café.',
    name: 'Diane Uwase',
    role: 'Owner',
    business: 'Ikawa Corner Café',
    location: 'Kacyiru, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1d46ac71-ebbe-43ce-b496-ca33cb099f31.jpg',
    metric: { value: '−30%', label: 'less food waste in two months' },
  },
  products: ['sales-pos', 'inventory', 'expenses', 'reports'],
  plan: { name: 'Starter', audience: 'restaurants and cafés' },
  faq: [
    {
      question: 'Can I track orders by table?',
      answer: 'Yes. Open an order for each table or takeaway, add items over time, and close it when the guest pays.',
    },
    {
      question: 'Can I track ingredients, not just dishes?',
      answer: 'Yes. Set a simple recipe for each dish, and Kayko deducts the ingredients from stock with every sale.',
    },
    {
      question: 'Can guests split the bill?',
      answer: 'Yes. Split by item or amount, and take part in mobile money and part in cash on the same order.',
    },
    {
      question: 'Does it work if the internet goes down during service?',
      answer: 'Yes. Keep selling offline. Sales sync automatically when you’re back online.',
    },
    {
      question: 'Can I record market purchases and other daily costs?',
      answer: 'Yes. Log expenses with a photo of the receipt, so your profit reports include every cost.',
    },
  ],
  related: ['retail', 'hospitality', 'control-expenses'],
  cta: {
    headline: 'Run a smoother service, every day',
    subheadline: 'Set up your menu in minutes. Local support in Kigali when you need it.',
  },
}
